import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type Ref,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  GraduationCap,
  Image,
  Settings,
  Settings2,
  UserPlus,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router';

import { BreakableEmail } from '@/components/BreakableEmail';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { StudentViewErrorDialog } from '@/features/studentView/StudentViewErrorDialog';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';

import {
  imageSizeOverrideIncludesQuestions,
  isAutoNumberingDisabled,
  isDueSoonSectionDisabled,
  resolveImageSizeOverride,
  type CourseImageSizeOverride,
} from './courseDisplaySettings';
import { COURSE_AI_SETTINGS_TARGET_ID } from '@/features/productTours/tourDefinitions';
import { useTourTarget } from '@/features/productTours/useTourTarget';
import {
  PendingStaffInvites,
  StaffInviteSection,
  StaffRemoveButton,
  StaffRemoveConfirmation,
  type StaffRemoval,
} from '@/features/staffInvites/StaffInvitePanels';
import type { CourseStaffInvitesController } from '@/features/staffInvites/useCourseStaffInvites';
import { useOpenStudentView } from '@/features/studentView/useOpenStudentView';
import { AttemptsField } from '@/features/questions/AttemptsField';
import {
  attemptsToSelectValue,
  resolveSectionAttempts,
} from '@/features/questions/attemptSettings';
import {
  hintChoices,
  resolveHintSetting,
  resolveSolutionsSetting,
  SOLUTIONS_CHOICES,
} from '@/features/questions/questionTimingSettings';

import type {
  CourseHomepage,
  CourseStaffMember,
  RawQuestionSettings,
} from './useCourse';
import type {
  CourseControlPanelActions,
  CourseControlPanelUpdatePayload,
  CourseSearchUser,
} from './useCourseControlPanelActions';
import styles from './CourseControlPanel.module.css';

const CONTROL_PANEL_EXPANDED_STORAGE_KEY =
  'kudu.courseHomepage.controlPanelExpanded';

interface CourseControlPanelProps {
  actions: CourseControlPanelActions;
  course: CourseHomepage;
  courseId?: string;
  // Called as View Roster/Grades opens the gradebook.
  onOpenRoster?: () => void;
  rosterStudentCount?: number;
  // Invites and staff removal in the Add Instructor dialog. Without it the
  // dialog offers only searching and adding existing instructors.
  staffInvites?: CourseStaffInvitesController;
}

type StatusAction = 'set-live' | 'set-closed' | 'reopen';

interface QuestionSettingsFormState {
  attemptsExactAnswer: number;
  attemptsMultipleChoice: number;
  hintsExactAnswer: number;
  hintsMultipleChoice: number;
  showPracticeSolutionsSetting: number;
  showSolutionsAfterInterval: number;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

function normalizeNumber(value: unknown, fallback: number) {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : fallback;

  return Number.isFinite(parsed) ? parsed : fallback;
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function getStaffCountLabel(course: CourseHomepage) {
  const instructorCount =
    course.secondaryInstructorIds?.length ?? course.secondaryInstructors?.length ?? 0;
  const assistantCount = course.assistantIds?.length ?? course.assistants?.length ?? 0;

  return `${pluralize(
    instructorCount,
    'additional instructor',
  )} / ${pluralize(assistantCount, 'assistant')}`;
}

export function getCourseStatusText(course: CourseHomepage) {
  if (course.isLive && course.isComplete) {
    return 'Course enrollment is closed';
  }

  if (course.isLive) {
    return 'Course is open to enrollment';
  }

  return 'Course is not open to enrollment. Set Live to open';
}

function getCourseStatusAction(course: CourseHomepage): StatusAction {
  if (!course.isLive) {
    return 'set-live';
  }

  return course.isComplete ? 'reopen' : 'set-closed';
}

function getStatusActionLabel(action: StatusAction) {
  switch (action) {
    case 'set-live':
      return 'Set Live';
    case 'set-closed':
      return 'Set Complete';
    case 'reopen':
      return 'Reopen Course';
  }
}

function getStoredExpandedState() {
  if (typeof window === 'undefined') {
    return false;
  }

  const storedValue = window.localStorage.getItem(CONTROL_PANEL_EXPANDED_STORAGE_KEY);

  if (storedValue === 'true') {
    return true;
  }

  if (storedValue === 'false') {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  return window.innerWidth >= 768;
}

function getCourseStatusIcon(course: CourseHomepage) {
  if (course.isLive && course.isComplete) {
    return EyeOff;
  }

  return course.isLive ? Eye : EyeOff;
}

function ControlPanelRow({
  actions,
  icon,
  label,
}: {
  actions: ReactNode;
  icon: ReactNode;
  label: string;
}) {
  return (
    <div className={styles.row}>
      <span aria-hidden="true" className={styles.rowIcon}>
        {icon}
      </span>
      <div className={styles.rowContent}>
        <div className={styles.actions}>{actions}</div>
        <p className={styles.rowTitle}>{label}</p>
      </div>
    </div>
  );
}

function getStatusDialogCopy(action: StatusAction) {
  switch (action) {
    case 'set-live':
      return {
        body: 'This will make the course visible to enrolled students. Continue?',
        title: 'Set course live?',
      };
    case 'set-closed':
      return {
        body: 'This will close the course. Students will not be able to submit new answers. Continue?',
        title: 'Set course closed?',
      };
    case 'reopen':
      return {
        body: 'This will reopen the course for student submissions. Continue?',
        title: 'Reopen course?',
      };
  }
}

function getStatusUpdatePayload(action: StatusAction): CourseControlPanelUpdatePayload {
  switch (action) {
    case 'set-live':
      return {
        is_complete: false,
        is_live: true,
      };
    case 'set-closed':
      return {
        is_complete: true,
      };
    case 'reopen':
      return {
        is_complete: false,
      };
  }
}

function getInitialQuestionSettings(
  settings: RawQuestionSettings | undefined,
): QuestionSettingsFormState {
  // The course is the top level: what it lacks is the default of 1 attempt.
  const attempts = resolveSectionAttempts(null, settings);

  return {
    attemptsExactAnswer: attemptsToSelectValue(attempts.exactAnswer.limit),
    attemptsMultipleChoice: attemptsToSelectValue(attempts.multipleChoice.limit),
    hintsExactAnswer: resolveHintSetting(null, settings, 'exactAnswer').value,
    hintsMultipleChoice: resolveHintSetting(null, settings, 'multipleChoice').value,
    showPracticeSolutionsSetting: normalizeNumber(
      settings?.show_practice_solutions_setting ??
        settings?.showPracticeSolutionsSetting,
      0,
    ),
    showSolutionsAfterInterval: resolveSolutionsSetting(null, settings).value,
  };
}

// From the value the dialog opened with, so a stored value outside the list
// stays choosable after another is picked.
function renderHintOptions(initialValue: number) {
  return hintChoices(initialValue).map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ));
}

function getStaffMembers(
  members: CourseStaffMember[] | undefined,
  ids: string[] | undefined,
) {
  if (members && members.length > 0) {
    return members;
  }

  return (ids ?? []).map((id) => ({
    id,
    name: id,
  }));
}

function StaffList({
  emptyLabel,
  members,
  renderActions,
  title,
  titleRef,
}: {
  emptyLabel: string;
  members: CourseStaffMember[];
  renderActions?: (member: CourseStaffMember) => ReactNode;
  title: string;
  // Takes focus after someone is removed from this list.
  titleRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    <div className={styles.staffList}>
      <h4 className={styles.staffTitle} ref={titleRef} tabIndex={-1}>
        {title}
      </h4>
      {members.length > 0 ? (
        <ul className={styles.compactList}>
          {members.map((member) =>
            renderActions ? (
              <li className={styles.staffMemberRow} key={member.id}>
                <span className={styles.staffMemberText}>
                  {member.name}
                  {member.email ? (
                    <span className={styles.mutedText}>
                      {' '}
                      (<BreakableEmail address={member.email} />)
                    </span>
                  ) : null}
                </span>
                {renderActions(member)}
              </li>
            ) : (
              <li key={member.id}>
                {member.name}
                {member.email ? (
                  <span className={styles.mutedText}>
                    {' '}
                    (<BreakableEmail address={member.email} />)
                  </span>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : (
        <p className={styles.mutedText}>{emptyLabel}</p>
      )}
    </div>
  );
}

function EnrollmentInstructionsDialog({
  course,
  onOpenChange,
  open,
}: {
  course: CourseHomepage;
  onOpenChange(open: boolean): void;
  open: boolean;
}) {
  // The public site, not window.location.origin: an instructor may be reading
  // this on a staging or preview host, and these steps are copied into a
  // syllabus for students who will only ever use the production site.
  const courseUrl = `www.kudu.com/app/courses/${course.id}`;
  const focusReturn = useDialogFocusReturn();

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>
            Enrollment Instructions
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>
              Share these steps with your students so they can enroll in this
              course.
            </p>
          </Dialog.Description>
          <ol className={styles.enrollmentSteps}>
            <li>
              Go to{' '}
              <a
                href="https://www.kudu.com/app"
                rel="noreferrer"
                target="_blank"
              >
                www.kudu.com/app
              </a>{' '}
              and create a new account.
            </li>
            <li>Click &ldquo;Add Course&rdquo; in the top right.</li>
            <li>
              Search for the course ID{' '}
              <span className={styles.monospace}>{course.id}</span> and click
              &ldquo;Sign Up&rdquo;.
            </li>
            <li>
              Pay with a credit card or use the access code from your bookstore.
            </li>
            <li>
              You can access your course directly at{' '}
              <span className={styles.monospace}>{courseUrl}</span>
            </li>
          </ol>
          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button variant="primary">Done</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const IMAGE_SIZE_OPTIONS: Array<{
  hint: string;
  label: string;
  value: CourseImageSizeOverride;
}> = [
  {
    hint: 'Each image keeps the size it was given when it was written.',
    label: 'Default',
    value: 'authored',
  },
  { hint: 'Every image is shown small.', label: 'All small', value: 'small' },
  { hint: 'Every image is shown medium.', label: 'All medium', value: 'medium' },
  { hint: 'Every image is shown large.', label: 'All large', value: 'large' },
];

// The numbering the proto uses. Kept here rather than derived so the wire format
// is visible at the point it is written.
const IMAGE_SIZE_WIRE_VALUE: Record<CourseImageSizeOverride, number> = {
  authored: 0,
  large: 3,
  medium: 2,
  small: 1,
};

function DisplaySettingsDialog({
  actions,
  course,
  onOpenChange,
  open,
}: {
  actions: CourseControlPanelProps['actions'];
  course: CourseHomepage;
  onOpenChange(open: boolean): void;
  open: boolean;
}) {
  const settings = course.courseSettings ?? {};
  const [form, setForm] = useState(() => ({
    autoNumbering: !isAutoNumberingDisabled(settings),
    dueSoonSummary: !isDueSoonSectionDisabled(settings),
    imageSize: resolveImageSizeOverride(settings),
    imageSizeIncludesQuestions: imageSizeOverrideIncludesQuestions(settings),
  }));
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const focusReturn = useDialogFocusReturn();

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setForm({
      autoNumbering: !isAutoNumberingDisabled(settings),
      dueSoonSummary: !isDueSoonSectionDisabled(settings),
      imageSize: resolveImageSizeOverride(settings),
      imageSizeIncludesQuestions: imageSizeOverrideIncludesQuestions(settings),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.courseSettings, open]);

  async function handleSave() {
    setError(null);
    setPending(true);

    try {
      // Spread the existing settings first: UpdateCourse REPLACES the whole
      // CourseSettings message rather than merging fields, so anything not sent
      // here is erased.
      await actions.updateCourse({
        course_settings: {
          ...settings,
          disable_auto_numbering: !form.autoNumbering,
          disable_due_soon_section: !form.dueSoonSummary,
          image_size_override: IMAGE_SIZE_WIRE_VALUE[form.imageSize],
          image_size_override_includes_questions: form.imageSizeIncludesQuestions,
        },
      });
      onOpenChange(false);
    } catch (nextError) {
      setError(getErrorMessage(nextError));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>
            Display Settings
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>
              How this course looks to everyone reading it.
            </p>
          </Dialog.Description>

          <section className={styles.settingsSection}>
            <h3 className={styles.settingsSectionTitle}>
              Homepage display settings
            </h3>
            <label className={styles.checkboxRow}>
              <input
                checked={form.autoNumbering}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    autoNumbering: event.target.checked,
                  }));
                }}
                type="checkbox"
              />
              <span>
                <span className={styles.checkboxLabel}>Auto-number sections</span>
                <span className={styles.checkboxBlurb}>
                  Sections will be auto numbered from 1. Disable if you want
                  custom numbering in your titles.
                </span>
              </span>
            </label>
            <label className={styles.checkboxRow}>
              <input
                checked={form.dueSoonSummary}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    dueSoonSummary: event.target.checked,
                  }));
                }}
                type="checkbox"
              />
              <span>
                <span className={styles.checkboxLabel}>Show due soon summary</span>
                <span className={styles.checkboxBlurb}>
                  A compact summary is shown on the course homepage for any
                  sections with assignments due
                </span>
              </span>
            </label>
          </section>

          <section className={styles.settingsSection}>
            <h3 className={styles.settingsSectionTitle}>
              Chapter/Component Display Settings
            </h3>
            <fieldset className={styles.settingsFieldset}>
              <legend className={styles.checkboxLabel}>Image size</legend>
              <p className={styles.checkboxBlurb}>
                Changes how images are displayed. It does not change the size
                stored on any image, and readers can still resize a figure for
                themselves.
              </p>
              {IMAGE_SIZE_OPTIONS.map((option) => (
                <label className={styles.checkboxRow} key={option.value}>
                  <input
                    checked={form.imageSize === option.value}
                    name="course-image-size"
                    onChange={() => {
                      setForm((current) => ({
                        ...current,
                        imageSize: option.value,
                      }));
                    }}
                    type="radio"
                  />
                  <span>
                    <span className={styles.checkboxLabel}>{option.label}</span>
                    <span className={styles.checkboxBlurb}>{option.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            <label className={styles.checkboxRow}>
              <input
                checked={form.imageSizeIncludesQuestions}
                disabled={form.imageSize === 'authored'}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    imageSizeIncludesQuestions: event.target.checked,
                  }));
                }}
                type="checkbox"
              />
              <span>
                <span className={styles.checkboxLabel}>
                  Also apply to images inside questions
                </span>
                <span className={styles.checkboxBlurb}>
                  Covers question prompts, answer options, hints and solutions.
                  Without this, the size above applies to chapter content only.
                </span>
              </span>
            </label>
          </section>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button
              disabled={pending}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              Save
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CourseStatusConfirmationDialog({
  action,
  actions,
  onOpenChange,
  open,
}: {
  action: StatusAction;
  actions: CourseControlPanelActions;
  onOpenChange(open: boolean): void;
  open: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const copy = getStatusDialogCopy(action);
  const actionLabel = getStatusActionLabel(action);
  const focusReturn = useDialogFocusReturn();

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open, action]);

  async function handleConfirm() {
    setError(null);

    try {
      await actions.updateCourse(getStatusUpdatePayload(action));
      onOpenChange(false);
    } catch (nextError) {
      setError(getErrorMessage(nextError));
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>{copy.title}</Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>{copy.body}</p>
          </Dialog.Description>
          {error ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button disabled={actions.isUpdating} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={actions.isUpdating}
              onClick={() => {
                void handleConfirm();
              }}
              variant="primary"
            >
              {actions.isUpdating ? 'Saving...' : actionLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AddInstructorDialog({
  actions,
  course,
  onOpenChange,
  open,
  staffInvites,
}: {
  actions: CourseControlPanelActions;
  course: CourseHomepage;
  onOpenChange(open: boolean): void;
  open: boolean;
  staffInvites?: CourseStaffInvitesController;
}) {
  const searchInputId = useId();
  const fullRightsId = useId();
  const focusReturn = useDialogFocusReturn();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fullEditRights, setFullEditRights] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState<CourseSearchUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  // The email of the last completed search, and a counter that remounts the
  // invite section (clearing its form) on every new search.
  const [searchedEmail, setSearchedEmail] = useState('');
  const [searchCount, setSearchCount] = useState(0);
  const [staffStatus, setStaffStatus] = useState('');
  // Who the confirmation pop-up is asking about, if anyone.
  const [removal, setRemoval] = useState<StaffRemoval | null>(null);
  const instructorsTitleRef = useRef<HTMLHeadingElement>(null);
  const assistantsTitleRef = useRef<HTMLHeadingElement>(null);
  const refreshStaff = staffInvites?.refresh;
  const canRemoveStaff = Boolean(staffInvites?.canRemoveStaff);
  const noMatchEmail =
    staffInvites &&
    hasSearched &&
    !error &&
    !actions.isSearching &&
    results.length === 0 &&
    searchedEmail
      ? searchedEmail
      : null;
  const secondaryInstructors = getStaffMembers(
    course.secondaryInstructors,
    course.secondaryInstructorIds,
  );
  const assistants = getStaffMembers(course.assistants, course.assistantIds);

  useEffect(() => {
    if (!open) {
      return;
    }

    setEmail('');
    setError(null);
    setFullEditRights(false);
    setHasSearched(false);
    setResults([]);
    setSelectedUserId(null);
    setSearchedEmail('');
    setStaffStatus('');
    setRemoval(null);
  }, [open]);

  // The lists below must be current each time the dialog opens: someone may
  // have accepted an invite since this page loaded.
  useEffect(() => {
    if (open) {
      refreshStaff?.();
    }
  }, [open, refreshStaff]);

  async function handleSearch(event?: FormEvent) {
    event?.preventDefault();
    setError(null);
    setHasSearched(true);
    setSelectedUserId(null);

    try {
      const nextResults = await actions.searchUsers(email);
      setResults(nextResults);
      setSelectedUserId(nextResults[0]?.id ?? null);
      setSearchedEmail(email.trim());
      setSearchCount((count) => count + 1);
    } catch (nextError) {
      setResults([]);
      setError(getErrorMessage(nextError));
    }
  }

  async function handleAdd() {
    if (!selectedUserId) {
      return;
    }

    setError(null);

    if (selectedUserId === actions.currentUserId) {
      setError("You can't add yourself as course staff.");
      return;
    }

    if (secondaryInstructors.some((member) => member.id === selectedUserId)) {
      setError(
        'This user is already an additional instructor for this course.',
      );
      return;
    }

    if (assistants.some((member) => member.id === selectedUserId)) {
      setError('This user is already an assistant for this course.');
      return;
    }

    try {
      await actions.updateCourse(
        fullEditRights
          ? { new_secondary_instructor_id: selectedUserId }
          : { new_assistant_id: selectedUserId },
      );
      onOpenChange(false);
    } catch (nextError) {
      setError(getErrorMessage(nextError));
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>Add Instructor</Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>
              Search for an instructor account by email, then choose whether to
              grant full edit rights or add them as a read-only assistant.
            </p>
          </Dialog.Description>

          <form className={styles.searchForm} onSubmit={(event) => void handleSearch(event)}>
            <label className={styles.field} htmlFor={searchInputId}>
              <span>Search by email</span>
              <input
                id={searchInputId}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="instructor@example.edu"
                type="email"
                value={email}
              />
            </label>
            <Button disabled={actions.isSearching} type="submit" variant="secondary">
              {actions.isSearching ? 'Searching...' : 'Search'}
            </Button>
          </form>

          <div className={styles.resultsArea}>
            <h3 className={styles.sectionTitle}>Search results</h3>
            {results.length > 0 ? (
              <div className={styles.resultList} role="radiogroup">
                {results.map((user) => (
                  <label className={styles.resultOption} key={user.id}>
                    <input
                      checked={selectedUserId === user.id}
                      name="selected-instructor"
                      onChange={() => setSelectedUserId(user.id)}
                      type="radio"
                      value={user.id}
                    />
                    <span>
                      {user.name}
                      {user.email ? (
                        <span className={styles.mutedText}> ({user.email})</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            ) : hasSearched && !error && !actions.isSearching ? (
              <p className={styles.mutedText}>No instructor found with that email.</p>
            ) : (
              <p className={styles.mutedText}>Search results will appear here.</p>
            )}
          </div>

          {staffInvites && noMatchEmail ? (
            <StaffInviteSection
              controller={staffInvites}
              courseTitle={course.title}
              email={noMatchEmail}
              key={searchCount}
              role={fullEditRights ? 'secondary_instructor' : 'assistant'}
            />
          ) : null}

          <label className={styles.checkboxRow} htmlFor={fullRightsId}>
            <input
              checked={fullEditRights}
              id={fullRightsId}
              onChange={(event) => setFullEditRights(event.target.checked)}
              type="checkbox"
            />
            <span>
              Grant full edit rights
              <small>
                If unchecked, the user is added as an assistant with read-only
                access to answers and stats.
              </small>
            </span>
          </label>

          <div className={styles.staffGrid}>
            <StaffList
              emptyLabel="No additional instructors."
              members={secondaryInstructors}
              renderActions={
                canRemoveStaff
                  ? (member) => (
                      <StaffRemoveButton
                        memberName={member.name}
                        onClick={() =>
                          setRemoval({
                            memberId: member.id,
                            memberName: member.name,
                            role: 'secondary_instructor',
                          })
                        }
                      />
                    )
                  : undefined
              }
              title="Current additional instructors"
              titleRef={instructorsTitleRef}
            />
            <StaffList
              emptyLabel="No assistants."
              members={assistants}
              renderActions={
                canRemoveStaff
                  ? (member) => (
                      <StaffRemoveButton
                        memberName={member.name}
                        onClick={() =>
                          setRemoval({
                            memberId: member.id,
                            memberName: member.name,
                            role: 'assistant',
                          })
                        }
                      />
                    )
                  : undefined
              }
              title="Current assistants"
              titleRef={assistantsTitleRef}
            />
          </div>

          {staffInvites &&
          !canRemoveStaff &&
          secondaryInstructors.length + assistants.length > 0 ? (
            <p className={styles.staffNote}>
              {course.instructor?.name
                ? `Only ${course.instructor.name}, the course’s main instructor, can remove course staff.`
                : 'Only the course’s main instructor can remove course staff.'}
            </p>
          ) : null}

          {staffInvites ? (
            <p aria-live="polite" className={styles.staffStatus}>
              {staffStatus}
            </p>
          ) : null}

          {staffInvites ? (
            <StaffRemoveConfirmation
              courseTitle={course.title}
              focusAfterRemoval={(removed) =>
                (removed.role === 'assistant'
                  ? assistantsTitleRef
                  : instructorsTitleRef
                ).current?.focus()
              }
              onClose={() => setRemoval(null)}
              onRemove={staffInvites.removeStaffMember}
              onRemoved={(removed) => {
                setRemoval(null);
                setStaffStatus(`${removed.memberName} was removed from course staff`);
              }}
              removal={removal}
            />
          ) : null}

          {staffInvites ? <PendingStaffInvites controller={staffInvites} /> : null}

          {error ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button disabled={actions.isUpdating} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={!selectedUserId || actions.isUpdating}
              onClick={() => {
                void handleAdd();
              }}
              variant="primary"
            >
              {actions.isUpdating ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Course-level AI defaults. There is no "inherit" here — the course IS the
// default; a chapter may override any of these individually in its own AI
// settings, and leaving a chapter setting on "Use course default" keeps it
// following whatever is set here.
function CourseAISettingsDialog({
  actions,
  course,
  onOpenChange,
  open,
}: {
  actions: CourseControlPanelActions;
  course: CourseHomepage;
  onOpenChange(open: boolean): void;
  open: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const focusReturn = useDialogFocusReturn();
  const settings = course.courseQuestionSettings as
    | Record<string, unknown>
    | undefined;

  const readForm = () => {
    const flag = (snake: string, camel: string) =>
      Number(settings?.[snake] ?? settings?.[camel] ?? 0) === 1 ? 1 : 0;
    const attempts = Number(
      settings?.num_attempts_to_show_ai_hints ??
        settings?.numAttemptsToShowAiHints ??
        0,
    );
    return {
      aiHintAttempts: Number.isFinite(attempts) && attempts > 0 ? attempts : 0,
      allowAiAssistant: flag('allow_ai_assistant', 'allowAiAssistant'),
      allowAiHints: flag('allow_ai_hints', 'allowAiHints'),
      allowAiPractice: flag('allow_ai_practice_problems', 'allowAiPracticeProblems'),
    };
  };

  const [form, setForm] = useState(readForm);

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    setForm(readForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.courseQuestionSettings, open]);

  async function handleSave() {
    setError(null);
    try {
      // Spread the existing settings so saving AI defaults never wipes the
      // course's other question defaults.
      await actions.updateCourse({
        course_question_settings: {
          ...(course.courseQuestionSettings ?? {}),
          allow_ai_assistant: form.allowAiAssistant,
          allow_ai_hints: form.allowAiHints,
          allow_ai_practice_problems: form.allowAiPractice,
          num_attempts_to_show_ai_hints: form.aiHintAttempts,
        } as RawQuestionSettings,
      });
      onOpenChange(false);
    } catch (nextError) {
      setError(getErrorMessage(nextError));
    }
  }

  const flagField = (
    label: string,
    value: number,
    onChange: (next: number) => void,
  ) => (
    <label className={styles.field}>
      <span>{label}</span>
      <select onChange={(event) => onChange(Number(event.target.value))} value={value}>
        <option value={0}>Off</option>
        <option value={1}>On</option>
      </select>
    </label>
  );

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>
            Course AI Settings
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>
              Defaults for every chapter in this course. A chapter can override
              any of them in its own AI settings.
            </p>
          </Dialog.Description>

          <div className={styles.formGrid}>
            {flagField('AI generated hints', form.allowAiHints, (next) =>
              setForm((current) => ({ ...current, allowAiHints: next })),
            )}

            <label className={styles.field}>
              <span>Show AI hints after attempts</span>
              <select
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    aiHintAttempts: Number(event.target.value),
                  }))
                }
                value={form.aiHintAttempts}
              >
                <option value={0}>Right away</option>
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    After {value} attempt{value === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </label>

            {flagField('AI practice problems', form.allowAiPractice, (next) =>
              setForm((current) => ({ ...current, allowAiPractice: next })),
            )}
            {flagField('Kai AI assistant in chapters', form.allowAiAssistant, (next) =>
              setForm((current) => ({ ...current, allowAiAssistant: next })),
            )}
          </div>

          {error ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button
              disabled={actions.isUpdating}
              onClick={handleSave}
              variant="primary"
            >
              {actions.isUpdating ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CourseQuestionSettingsDialog({
  actions,
  course,
  onOpenChange,
  open,
}: {
  actions: CourseControlPanelActions;
  course: CourseHomepage;
  onOpenChange(open: boolean): void;
  open: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<QuestionSettingsFormState>(() =>
    getInitialQuestionSettings(course.courseQuestionSettings),
  );
  const focusReturn = useDialogFocusReturn();

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setFormState(getInitialQuestionSettings(course.courseQuestionSettings));
  }, [course.courseQuestionSettings, open]);

  // What the dialog opened with, for the line under each attempts field.
  const initialState = getInitialQuestionSettings(course.courseQuestionSettings);
  const attempts = resolveSectionAttempts(null, course.courseQuestionSettings);

  async function handleSave() {
    const courseQuestionSettings: RawQuestionSettings = {
      // UpdateCourse replaces the course's settings whole. Without what is
      // already there, saving here deleted the course's AI defaults.
      ...(course.courseQuestionSettings ?? {}),
      // Saved explicitly, the default of 1 included (issue #54).
      num_attempts_exact_answer: formState.attemptsExactAnswer,
      num_attempts_multiple_choice: formState.attemptsMultipleChoice,
      num_attempts_to_show_hints_exact_answer: formState.hintsExactAnswer,
      num_attempts_to_show_hints_multiple_choice: formState.hintsMultipleChoice,
      show_practice_solutions_setting: formState.showPracticeSolutionsSetting,
      show_solutions_after_interval: formState.showSolutionsAfterInterval,
    };

    setError(null);

    try {
      await actions.updateCourse({
        course_question_settings: courseQuestionSettings,
      });
      onOpenChange(false);
    } catch (nextError) {
      setError(getErrorMessage(nextError));
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>
            Course Question Settings
          </Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>
              These course-level homework defaults apply to components unless a
              component overrides them.
            </p>
          </Dialog.Description>

          <div className={styles.formGrid}>
            <AttemptsField
              field="multipleChoice"
              initialSource={attempts.multipleChoice.source}
              initialValue={initialState.attemptsMultipleChoice}
              label="Number of attempts for multiple choice questions"
              labelClassName={styles.field}
              level="course"
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  attemptsMultipleChoice: value,
                }))
              }
              value={formState.attemptsMultipleChoice}
            />

            <AttemptsField
              field="exactAnswer"
              initialSource={attempts.exactAnswer.source}
              initialValue={initialState.attemptsExactAnswer}
              label="Number of attempts for exact/numerical answer questions"
              labelClassName={styles.field}
              level="course"
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  attemptsExactAnswer: value,
                }))
              }
              value={formState.attemptsExactAnswer}
            />

            <label className={styles.field}>
              <span>Hints for multiple choice questions</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    hintsMultipleChoice: Number(event.target.value),
                  }))
                }
                value={formState.hintsMultipleChoice}
              >
                {renderHintOptions(initialState.hintsMultipleChoice)}
              </select>
            </label>

            <label className={styles.field}>
              <span>Hints for exact/numerical questions</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    hintsExactAnswer: Number(event.target.value),
                  }))
                }
                value={formState.hintsExactAnswer}
              >
                {renderHintOptions(initialState.hintsExactAnswer)}
              </select>
            </label>

            <label className={styles.field}>
              <span>When students see solutions</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    showSolutionsAfterInterval: Number(event.target.value),
                  }))
                }
                value={formState.showSolutionsAfterInterval}
              >
                {SOLUTIONS_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              <span>Show solutions to unassigned practice problems</span>
              <select
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    showPracticeSolutionsSetting: Number(event.target.value),
                  }))
                }
                value={formState.showPracticeSolutionsSetting}
              >
                <option value={0}>Always</option>
                <option value={1}>After homework due date</option>
              </select>
            </label>
          </div>

          {error ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.dialogFooter}>
            <Dialog.Close asChild>
              <Button disabled={actions.isUpdating} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={actions.isUpdating}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {actions.isUpdating ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CourseControlPanel({
  actions,
  course,
  courseId,
  onOpenRoster,
  rosterStudentCount,
  staffInvites,
}: CourseControlPanelProps) {
  const [addInstructorOpen, setAddInstructorOpen] = useState(false);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => getStoredExpandedState());
  const [questionSettingsOpen, setQuestionSettingsOpen] = useState(false);
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const panelBodyId = useId();
  const courseAiSettingsTargetRef = useTourTarget(COURSE_AI_SETTINGS_TARGET_ID);
  const navigate = useNavigate();
  const openStudentView = useOpenStudentView();
  const statusAction = getCourseStatusAction(course);
  const statusActionLabel = getStatusActionLabel(statusAction);
  const studentCount = rosterStudentCount ?? course.studentCount ?? 0;
  const StatusIcon = getCourseStatusIcon(course);

  function toggleExpanded() {
    setIsExpanded((currentExpanded) => {
      const nextExpanded = !currentExpanded;

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          CONTROL_PANEL_EXPANDED_STORAGE_KEY,
          String(nextExpanded),
        );
      }

      return nextExpanded;
    });
  }

  if (course.userRole !== 'instructor') {
    return null;
  }

  return (
    <>
      <Card
        aria-label="Course control panel"
        as="section"
        className={styles.panel}
        data-testid="course-control-panel"
      >
        <button
          aria-controls={panelBodyId}
          aria-expanded={isExpanded}
          className={styles.panelHeader}
          onClick={toggleExpanded}
          type="button"
        >
          <span className={styles.headerTitleGroup}>
            <Settings2 aria-hidden="true" className={styles.headerIcon} size={16} />
            <span className={styles.panelTitle}>Control Panel</span>
          </span>
          {isExpanded ? (
            <ChevronUp aria-hidden="true" className={styles.chevron} size={16} />
          ) : (
            <ChevronDown aria-hidden="true" className={styles.chevron} size={16} />
          )}
        </button>

        <div
          className={styles.bodyShell}
          data-expanded={isExpanded ? 'true' : 'false'}
          id={panelBodyId}
        >
          {isExpanded ? (
            <div className={styles.rows}>
              <ControlPanelRow
                icon={<StatusIcon size={16} strokeWidth={2} />}
                label={getCourseStatusText(course)}
                actions={
                  <>
                    <button
                      className={styles.actionButton}
                      onClick={() => setStatusDialogOpen(true)}
                      type="button"
                    >
                      {statusActionLabel}
                    </button>
                    <button
                      className={styles.actionButton}
                      onClick={() => setEnrollmentOpen(true)}
                      type="button"
                    >
                      Enrollment Instructions
                    </button>
                  </>
                }
              />

              <ControlPanelRow
                icon={<Users size={16} strokeWidth={2} />}
                label={`${pluralize(studentCount, 'student')} registered`}
                actions={
                  <button
                    className={styles.actionButton}
                    onClick={() => {
                      if (!courseId) {
                        return;
                      }

                      onOpenRoster?.();
                      navigate(`/app/courses/${courseId}/grades`);
                    }}
                    type="button"
                  >
                    View Roster/Grades ({studentCount})
                  </button>
                }
              />

              <ControlPanelRow
                icon={<UserPlus size={16} strokeWidth={2} />}
                label={getStaffCountLabel(course)}
                actions={
                  <button
                    className={styles.actionButton}
                    onClick={() => setAddInstructorOpen(true)}
                    type="button"
                  >
                    Add Instructor
                  </button>
                }
              />

              <ControlPanelRow
                icon={<Image size={16} strokeWidth={2} />}
                label="How this course looks to readers"
                actions={
                  <button
                    className={styles.actionButton}
                    onClick={() => setDisplaySettingsOpen(true)}
                    type="button"
                  >
                    Display Settings
                  </button>
                }
              />

              <ControlPanelRow
                icon={<Settings size={16} strokeWidth={2} />}
                label="Course question defaults"
                actions={
                  <button
                    className={styles.actionButton}
                    onClick={() => setQuestionSettingsOpen(true)}
                    type="button"
                  >
                    Course Question Settings
                  </button>
                }
              />

              <ControlPanelRow
                icon={<Settings size={16} strokeWidth={2} />}
                label="Course AI defaults"
                actions={
                  <button
                    className={styles.actionButton}
                    onClick={() => setAiSettingsOpen(true)}
                    ref={courseAiSettingsTargetRef}
                    type="button"
                  >
                    Course AI Settings
                  </button>
                }
              />

              <ControlPanelRow
                icon={<GraduationCap size={16} strokeWidth={2} />}
                label="See the course exactly as a student"
                actions={
                  <button
                    className={styles.actionButton}
                    disabled={openStudentView.isPending}
                    onClick={() => {
                      if (!courseId) {
                        return;
                      }

                      openStudentView.mutate({ courseId });
                    }}
                    type="button"
                  >
                    View as Student
                  </button>
                }
              />
            </div>
          ) : (
            <p className={styles.collapsedText}>
              Expand to view roster, change settings and other instructor controls
            </p>
          )}
        </div>
      </Card>

      <EnrollmentInstructionsDialog
        course={course}
        onOpenChange={setEnrollmentOpen}
        open={enrollmentOpen}
      />
      <DisplaySettingsDialog
        actions={actions}
        course={course}
        onOpenChange={setDisplaySettingsOpen}
        open={displaySettingsOpen}
      />
      <CourseStatusConfirmationDialog
        action={statusAction}
        actions={actions}
        onOpenChange={setStatusDialogOpen}
        open={statusDialogOpen}
      />
      <AddInstructorDialog
        actions={actions}
        course={course}
        onOpenChange={setAddInstructorOpen}
        open={addInstructorOpen}
        staffInvites={staffInvites}
      />
      <CourseQuestionSettingsDialog
        actions={actions}
        course={course}
        onOpenChange={setQuestionSettingsOpen}
        open={questionSettingsOpen}
      />
      <CourseAISettingsDialog
        actions={actions}
        course={course}
        onOpenChange={setAiSettingsOpen}
        open={aiSettingsOpen}
      />
      <StudentViewErrorDialog
        message={openStudentView.error?.message ?? null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            openStudentView.reset();
          }
        }}
        open={openStudentView.isError}
      />
    </>
  );
}
